import React from 'react';
import { FaRegSmile } from 'react-icons/fa';
import { BASE_URL } from '../Api';
import { Helmet, HelmetProvider } from "react-helmet-async";
import FreeHighlight from '../partials/FreeHighlight';
import { PluginDownloadButtons } from '../partials';

const SaudiArabiaEInvoicePlugin = () => {
  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>E-invoice in Saudi Arabia - Aronium</title>
          <meta name="description" content="Stay compliant with Saudi Arabia E-invoice phase 1 requirements" />
        </Helmet>
      </HelmetProvider>

      <section className="py-8 max-w-screen-xl px-4 mx-auto">
        <div className="max-w-screen-xl mx-auto text-center">
          <h1 className="text-4xl mb-4">
            Comply with E-Invoicing in
          </h1>
          <h1 className="text-4xl mb-4">
            العربية السعودية
          </h1>
          <p>
            Simplify your invoicing process and ensure compliance with ZATCA’s e-invoicing requirements.
          </p>

          <p className='my-8'><img
            alt="Saudi Arabia E-invoicing phase 1"
            src="/images/zatca-logo-dark.svg"
            className="mx-auto h-24 md:h-32 transition-transform duration-300 hover:scale-110"
          />
          </p>

          {/* Call to Action Buttons */}
          <PluginDownloadButtons
            liteUrl={`${BASE_URL}/plugins/e-invoice-ksa/latest/Aronium.Pos.EInvoice.Ksa.Lite.exe`}
            proUrl={`${BASE_URL}/plugins/e-invoice-ksa/latest/Aronium.Pos.EInvoice.Ksa.Pro.exe`}
            className='my-8' />
        </div>
      </section>

      {/* Announcement Section */}
      <div className="bg-gradient-to-r from-[#cce3f0] to-[#a7b9d0] py-6 px-4 text-center flex items-center justify-center gap-4 shadow-lg">
        <FaRegSmile className="text-blue-600 w-8 h-8 animate-bounce" />
        <div className="flex flex-col sm:items-center md:items-start">
          <p className="text-xl text-gray-900 font-bold">📢 Exciting Update!</p>
          <p className="text-md text-gray-800">
            E-invoicing Phase 2 integration is ready! Read the announcement <a
              href="https://help.aronium.com/hc/en-us/community/posts/8828728190738"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              here
            </a>.
          </p>
        </div>
      </div>

      {/* Overview Section */}
      <div className="relative py-20 bg-gradient-to-r from-[#e7eff7] to-[#f5fafd]">
        <div className="relative max-w-7xl mx-auto text-center px-8 lg:px-12">
          <h2 className="text-5xl text-gray-800 leading-tight mb-8">
            Why use <span className='font-semibold'>Saudi Arabia E-invoice</span> plugin?
          </h2>
          <p className="text-lg text-gray-600 mx-auto">
            Ensure compliance with Saudi Arabia’s ZATCA e-invoicing regulations. Generate, manage, and validate invoices in a seamless, compliant way.
          </p>
        </div>
        <FreeHighlight className="mt-12" />
      </div>

      {/* Key Features Section */}
      <div className="py-24">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white p-8 shadow-lg rounded-lg border border-gray-100">
            <h3 className="text-2xl font-semibold text-gray-800">ZATCA compliance</h3>
            <p className="mt-4 text-gray-600">
              Meet all regulatory requirements for electronic invoicing as set by Saudi Arabia’s ZATCA.
            </p>
          </div>
          <div className="bg-white p-8 shadow-lg rounded-lg border border-gray-100">
            <h3 className="text-2xl font-semibold text-gray-800">Real-time invoice validation</h3>
            <p className="mt-4 text-gray-600">
              Ensure invoice integrity and validity with real-time validation checks and compliance tools.
            </p>
          </div>
          <div className="bg-white p-8 shadow-lg rounded-lg border border-gray-100">
            <h3 className="text-2xl font-semibold text-gray-800">QR Code generation</h3>
            <p className="mt-4 text-gray-600">
              Automatically generate QR codes for each invoice, allowing easy validation and ZATCA compliance.
            </p>
          </div>
          <div className="bg-white p-8 shadow-lg rounded-lg border border-gray-100">
            <h3 className="text-2xl font-semibold text-gray-800">Arabic and English support</h3>
            <p className="mt-4 text-gray-600">
              Generate invoices in both Arabic and English to accommodate a wider range of customers.
            </p>
          </div>
        </div>
      </div>
      {/* Call to Action Section */}
      <div className="relative bg-gradient-to-tr from-[#4a6fa6] to-[#a7b9d0] py-24 text-center text-white">
        <div className="relative max-w-6xl mx-auto px-4">
          <h2 className="text-4xl xl:text-6xl mb-6">
            Ready to simplify your E-invoicing?
          </h2>
          <p className="text-xl mb-10">
            Start using the <span className='font-semibold'>Saudi Arabia E-invoice</span> plugin for a seamless and compliant invoicing process.
          </p>
          <a
            href="https://help.aronium.com/hc/en-us/articles/4606198498834-About-E-invoice"
            className="button-rounded-white px-12 py-4"
            target="_blank" rel="noreferrer">
            Learn more <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="ml-2 w-6 h-6 transition-all">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
            </svg>
          </a>
        </div>
      </div>
    </>
  );
};

export default SaudiArabiaEInvoicePlugin;